import {createStore} from 'zustand/vanilla'
import {persist} from 'zustand/middleware'
import UserService from '@/services/http/user'
import {AxiosError} from 'axios'
import {BaseResponse} from '@/interfaces'
import {jwtDecode} from 'jwt-decode'
import {api} from '@/services/http/api'
import {ApiAbortControllerService} from '@/services/ApiAbortController'

export interface User {
  email: string
  nome: string
  cnpjs: string[]
  inscricaoEstadual: string
  razaoSocial: string
  admin: boolean
  iss: string
  sub: string
  aud: string
  iat: number
  exp: number
  role: string
}

export interface UserState {
  isLoading: boolean
  token?: string
  error?: AxiosError<BaseResponse<null>>
  user?: User
}

export interface UserActions {
  login: (email: string, password: string) => Promise<void>
  logout: () => void
}

export type UserStore = UserState & UserActions

export const initUserStore = (): UserState => {
  return {isLoading: false}
}

export const createUserStore = createStore<UserStore>()(
  persist(
    set => ({
      ...initUserStore(),
      login: async (email, password) => {
        try {
          set(() => ({isLoading: true, error: undefined}))
          const response = await UserService.login(email, password)
          const userData = jwtDecode<User>(response.Data.token)
          api.defaults.headers.common.Authorization = response.Data.token
          set(() => {
            return {
              isLoading: false,
              error: undefined,
              token: response.Data.token,
              user: userData,
            }
          })
        } catch (e) {
          const error = e as AxiosError<BaseResponse<null>>
          set(() => ({
            isLoading: false,
            error,
            token: undefined,
          }))
        }
      },
      logout: () => {
        ApiAbortControllerService.abort()
        set(() => ({
          isLoading: false,
          user: undefined,
          token: undefined,
          error: undefined,
        }))

        /**
         * This timeout is here to prevent the token
         * from being removed while a request is about to start,
         * yielding a 401 due lack of token instead of a cancelled request.
         * Yes, gambiarra. Num gostou vá dar parte.
         */
        setTimeout(() => {
          api.defaults.headers.common.Authorization = undefined
        }, 100)
      },
    }),
    {
      name: 'user',
      onRehydrateStorage: () => (state, error) => {
        if (state?.token) {
          api.defaults.headers.common.Authorization = state.token
        }
      },
      partialize: state => {
        return {token: state.token, user: state.user}
      },
    },
  ),
)
